import {Injectable} from '@angular/core';
import {catchError, finalize, map} from "rxjs/operators";
import {Observable, tap, throwError} from "rxjs";

import {IsEmptyString, IsEmptyVar} from "../../defines/common.func";
import {SearchParam} from "../../defines/client.const";
import {NormalizeSearchKeyword} from "../../defines/client.func";
import {PAGE, SASEUNG2_HANJA_API_URL} from "../../defines/protocol";
import {SaseungHanja} from "../../models/saseung.model";
import {ApiService} from "../api/api.service";
import {ListLoader} from "../../interfaces/list-loader";


export type SASEUNG_HANJAS = {
  totalCount: number;
  saseungHanjas: Array<SaseungHanja>;
};


@Injectable({
  providedIn: 'root'
})
export class SaseungHanjaService implements ListLoader<SaseungHanja> {
  protected urlApi = SASEUNG2_HANJA_API_URL;

  protected totalCount: number | null = null;
  protected listSaseung: Array<SaseungHanja> | null = null;
  private mapSaseung: Map<string, SaseungHanja> | null = null;
  private nextUrl: string | null = null
  private load$: Observable<boolean> | null = null;

  constructor(
    private api: ApiService,
  ) {
  }


  /******************************************************************************
   * ListLoader<Saseung> 구현부
   *****************************************************************************/
  public getList(): Array<SaseungHanja> | null {
    return this.listSaseung;
  }

  public getCurCount(): number | null {
    // @ts-ignore
    return this.listSaseung?.length ?? null;
  }

  public getTotalCount(): number | null {
    return this.totalCount;
  }

  public IsComplete(): boolean {
    // @ts-ignore
    return this.totalCount === 0 ||
      (this.listSaseung && !IsEmptyVar(this.totalCount) && this.listSaseung.length === this.totalCount);
  }

  public IsLoading(): boolean {
    return this.load$ !== null;
  }

  public getById(id: string): SaseungHanja | null {
    return this.mapSaseung?.get(id) ?? null;
  }

  public ResetList(param: SearchParam | null = null): void {
    this.listSaseung = null;
    this.mapSaseung = null;
    this.totalCount = null;

    const queryParams = new URLSearchParams();

    if (param) {
      if (param.hangul) queryParams.set('hangul', param.hangul);

      if (param.hanja) queryParams.set('hanja', param.hanja);
      else if (param.hun) queryParams.set('hun', param.hun);
      else if (param.keyword) {
        const keyword = NormalizeSearchKeyword(param.keyword);
        if (keyword) queryParams.set('search', keyword);
      }
    }

    this.nextUrl = queryParams.toString()
      ? `${this.urlApi}?${queryParams.toString()}`
      : this.urlApi;
  }

  public GetNextList(): Observable<boolean> {
    // console.log('SaseungHanjaService::GetNextList()', this.urlApi, this.nextUrl);

    if (IsEmptyString(this.nextUrl)) {
      return throwError({error: 'No more pages in SaseungList'});
    }

    if (this.load$) {
      return this.load$;
    }

    return this.load$ = this.api.Get(this.nextUrl!).pipe(
      catchError(err => {
        if ('status' in err && err.status === 404) {
          this.totalCount = 0;
        }
        throw err;
      }),
      finalize(() => {
        this.load$ = null;
      }),
      map((data: PAGE) => {
        // console.log('SaseungHanjaService::GetNextList.api.Get data =', data);

        let first = false;
        if (this.totalCount === null) {
          this.totalCount = data.count;
          this.listSaseung = new Array<SaseungHanja>();
          this.mapSaseung = new Map<string, SaseungHanja>();
          first = true;
        } else if (this.totalCount !== data.count) {
          console.error('[error]' + this.urlApi, data);
        }

        data.results.forEach(value => {
          const hanja = new SaseungHanja(value);
          this.listSaseung?.push(hanja);
          this.mapSaseung?.set(hanja.hangul, hanja);
        });
        // console.warn('SaseungHanjaService::GetNextList.api.Get()', this.listSaseung);

        this.nextUrl = data.next;

        return first;
      })
    );
  }

  /**
   * 훈 정보를 받아오는 함수.
   * 간편하게 첫 페이지만 받아서 반환한다.
   *
   * @param huns - 훈 정보를 받아올 훈들의 목록. 단일 값을 string으로 넣어도 된다.
   * @constructor
   */
  public GetHuns(hun: string): Observable<SASEUNG_HANJAS> {
    const url = `${SASEUNG2_HANJA_API_URL}?hun=${encodeURIComponent(hun)}`;
    console.log('SaseungHanjaService::GetHun() url =', url);


    return this.api.Get(url).pipe(
      tap(data => console.log('SaseungHanjaService::GetHun() Received data:', data)),
      map(data => {
        return {
          totalCount: data.count,
          saseungHanjas: data.results.map((item: any) => new SaseungHanja(item))
        };
      }),
      tap(processedData => console.log('SaseungHanjaService::GetHun() Processed data:', processedData)),
      catchError((error) => {
        console.error('Error in GetHun:', error);
        return throwError(() => new Error('SaseungHanjaService::GetHun() failed with error'));
      })
    );
  }

  public GetHanjas(hanja: string): Observable<SASEUNG_HANJAS> {
    const url = `${SASEUNG2_HANJA_API_URL}?hanja=${encodeURIComponent(hanja)}`;
    console.log('SaseungHanjaService::GetHanja() url =', url);

    return this.api.Get(url).pipe(
      tap(data => console.log('SaseungHanjaService::GetHanja() Received data:', data)),
      map(data => {
        return {
          totalCount: data.count,
          saseungHanjas: data.results.map((item: any) => new SaseungHanja(item))
        };
      }),
      tap(processedData => console.log('SaseungHanjaService::GetHanja() Processed data:', processedData)),
      catchError((error) => {
        console.error('Error in GetHanja:', error);
        return throwError(() => new Error('SaseungHanjaService::GetHanja() failed with error'));
      })
    );
  }
}
